import type { ReactNode } from 'react';
import { defineColumnShow } from '../shared/column-types';
import { searchContext } from '../../../../context/Context';
import dropdownLists from '../../DropdownLists';
import { renderMappingButton } from '../renders/buttonRenderers';
import { filterTypes } from '../../SetupTablesResources';
import { getGeneNCBIid } from '../renders/MappingFunctions';
import { renderRoundedValues, renderListsFromObjects } from '../renders/CommonAtributes';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter, createCapitalizeFilter } from '../filters/filterConfigs';
import { renderGeneNCBILink, renderEnsemblLinks } from '../renders/linkRenderers';
import { renderDefaultValueCols } from '@/utils/renderDefaultValueCols';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';
import { isArray } from 'lodash';
import { GtpCell } from '../components/GtpCell';
import { isRelease263Enabled } from '@/config/release263Features';

function normalizeNcbiIdString(rawId: any): string | undefined {
  if (rawId == null) return undefined;
  const normalized = String(rawId).trim();
  if (!normalized || normalized === '-' || normalized === '0') return undefined;
  return normalized;
}

function getRowGeneNcbiId(data: any): string | number | undefined {
  return (
    data?.ncbiID ||
    data?.geneNcbiID ||
    data?.gene_ncbi_id ||
    data?.gene_ncbi ||
    data?.geneID ||
    (Array.isArray(data?.geneNcbiIDs) ? data.geneNcbiIDs[0] : data?.geneNcbiIDs) ||
    undefined
  );
}

function getGeneMappingId(data: any): string | number | undefined {
  return data?.geneID || getRowGeneNcbiId(data);
}

function getGeneMappingName(data: any): string | undefined {
  return data?.symbol ?? data?.geneSymbol;
}

function getValidGeneInfoId(data: any): string | undefined {
  return normalizeNcbiIdString(getGeneMappingId(data));
}

type GeneNamePanelInfo = { symbol: string; id: string };

function GeneNamePanelButton({
  geneInfo,
  setGeneContent,
  children,
}: {
  geneInfo: GeneNamePanelInfo;
  setGeneContent: (c: any) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="tab-links p-0"
      onClick={() => {
        setGeneContent({ isActive: false, geneInfo: null });
        setTimeout(() => setGeneContent({ isActive: true, geneInfo }), 0);
      }}
      title="Show gene information"
    >
      {children}
    </button>
  );
}

export const getGeneColumns = context => {
  const { tab } = context;
  const headerProps = getHeaderProps();

  return [
    {
      name: 'symbol',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Symbol">Gene</span>,
      uniqueID: 'Gene',
      headerProps,
      columnSize: null,
      minWidth: 200,
      render: ({ data }) => {
        const { tab, setGeneContent } = searchContext();

        const symbolResult = renderTextColumn(data?.symbol);

        if (!symbolResult) return '-';

        if (tab === 'GENES') {
          const resolvedGeneId = getValidGeneInfoId(data);
          const geneInfo: GeneNamePanelInfo | undefined = resolvedGeneId
            ? { symbol: String(symbolResult), id: resolvedGeneId }
            : undefined;

          if (geneInfo) {
            return (
              <span className="geneModalLink">
                <GeneNamePanelButton geneInfo={geneInfo} setGeneContent={setGeneContent}>
                  {symbolResult}
                </GeneNamePanelButton>
              </span>
            );
          }

          return <span className="geneModalLink">{symbolResult}</span>;
        }

        return <span className="geneModalLink" />;
      },
    },

    {
      name: 'geneSymbol',
      category: 'Gene',
      sortable: true,
      filterType: filterTypes.stringWithContains,
      header: <span title="Gene Symbol">Gene</span>,
      headerProps,
      minWidth: 200,
      render: ({ data }) => {
        const { tab, setGeneContent } = searchContext();
        const delimiter = '|';

        const symbolResult = renderTextColumn(
          isArray(data?.geneSymbol) ? data?.geneSymbol[0] : data?.geneSymbol,
        );
        const ncbiResult = renderTextColumn(
          isArray(data?.geneNcbiID) ? data?.geneNcbiID[0] : data?.geneNcbiID,
        );

        let ids: any[] = [];
        let values: any[] = [];

        if (tab === 'GDA' || tab === 'DISEASES' || tab === 'GENES') {
          ids = [ncbiResult];
          values = [symbolResult];
        } else if (tab === 'DDA') {
          const idsResult = renderDefaultValueCols(data.geneNcbiID ? [data.geneNcbiID] : data.geneNcbiIDs);

          if (idsResult === '-') return '-';

          ids = idsResult;
          values = [symbolResult];
        } else if (tab === 'VDA') {
          const idsResult = renderDefaultValueCols(data.geneNcbiIDs);
          const valuesResult = renderDefaultValueCols(data.geneSymbol);

          if (idsResult === '-' || valuesResult === '-') return '-';

          ids = idsResult;
          values = valuesResult;
        } else if (tab === 'VARIANTS') {
          const variantGenes = renderDefaultValueCols(data.variantToGenes);

          if (variantGenes === '-') return '-';

          ids = variantGenes.map(el => el.geneNcbiID);
          values = variantGenes.map(el => el.geneSymbol);
        }

        if (!ids.length) return '-';

        const formatted = ids.map((id, index) => {
          const ncbiId = normalizeNcbiIdString(id);
          const displayText = values[index] ?? values[0];

          const key = `${id}-${index}`;

          if (!displayText || displayText === '-') {
            return (
              <span key={key}>-{index !== ids.length - 1 && <span className="px-1">{delimiter}</span>}</span>
            );
          }

          return (
            <span key={key}>
              {ncbiId ? (
                <GeneNamePanelButton
                  geneInfo={{ symbol: String(displayText), id: ncbiId }}
                  setGeneContent={setGeneContent}
                >
                  {displayText}
                </GeneNamePanelButton>
              ) : (
                displayText
              )}
              {index !== ids.length - 1 && <span className="px-1">{delimiter}</span>}
            </span>
          );
        });

        return <span className="geneModalLink">{formatted}</span>;
      },
    },

    {
      name: 'description',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab === 'GENES',
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'geneDescription',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab !== 'GENES',
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'gene_ncbi_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(getRowGeneNcbiId(data));
        if (result === '0') return '0';
        return <>{getGeneNCBIid(result)}</>;
      },
    },

    {
      name: 'geneNcbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(getRowGeneNcbiId(data));
        if (result === '0') return '0';
        return renderGeneNCBILink(result);
      },
    },

    {
      name: 'geneNcbiIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="Genes NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.geneNcbiIDs);
        if (result === '0') return '0';
        return (
          <>
            {result.map((id, index) => (
              <span key={id}>
                {getGeneNCBIid(String(id))}
                {index < result.length - 1 && ' | '}
              </span>
            ))}
          </>
        );
      },
    },

    {
      name: 'ncbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI identifier">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(getRowGeneNcbiId(data));
        if (result === '0') return '0';
        return renderGeneNCBILink(result);
      },
    },

    {
      name: 'gene_ensembl_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderEnsemblLinks(value),
    },

    {
      name: 'geneEnsemblIDs',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data, value }) => {
        const { setGeneContent } = searchContext();
        const result = renderDefaultValueCols(value);
        if (result === '-') return '-';
        const values = Array.isArray(result) ? result : [result];
        const geneId = getValidGeneInfoId(data);
        const symbol = String(getGeneMappingName(data) ?? data?.symbol ?? data?.geneSymbol ?? '');

        return values.map((ensemblId, index) => (
          <span key={`${ensemblId}-${index}`}>
            {geneId ? (
              <GeneNamePanelButton geneInfo={{ symbol, id: geneId }} setGeneContent={setGeneContent}>
                {ensemblId}
              </GeneNamePanelButton>
            ) : (
              ensemblId
            )}
            {index < values.length - 1 && <span className="px-1">|</span>}
          </span>
        ));
      },
    },

    {
      name: 'ensembl_ids',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderEnsemblLinks(value),
    },

    {
      name: 'geneNcbiType',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      minWidth: 125,
      ...createSelectFilter(dropdownLists.geneNcbiType),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'ncbi_type',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.ncbi_type),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneDPI',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Disease Pleiotropy Index for the gene">
          DPI<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneDSI',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Disease Specificity Index for the gene">
          DSI<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 180,
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'genepLI',
      category: 'Gene',
      sortable: true,
      header: <span title="Probability of being loss of function intolerant">pLI</span>,
      headerProps,
      render: ({ value }) => {
        const parsedValue = Number.parseFloat(value);
        if (!Number.isFinite(parsedValue)) {
          return renderDefaultValueCols(null);
        }
        const val = parsedValue.toExponential(4);
        return renderDefaultValueCols(val);
      },
    },

    {
      name: 'PLI',
      category: 'Gene',
      sortable: true,
      header: <span title="Probability of being loss of function intolerant">pLI</span>,
      headerProps,
      minWidth: 85,
      render: instance => renderRoundedValues(instance, 'PLI'),
    },

    {
      name: 'GTP',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Therapeutic Association Profile with TCES scores">GTAP</span>,
      headerProps,
      minWidth: 220,
      textEllipsis: true,
      show: ({ tab }) => isRelease263Enabled && (tab === 'GENES' || tab === 'GDA'),
      ...createSelectFilter(dropdownLists.GTP || []),
      render: ({ data, openRowDetails }) => <GtpCell data={data} openRowDetails={openRowDetails} />,
    },

    {
      name: 'geneProteinClassNames',
      category: 'Gene',
      sortable: true,
      header: <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>,
      minWidth: 150,
      headerProps,
      ...createSelectFilter(dropdownLists.geneProteinClassNames),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneProteinStrIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="UniProt Accession">UniProt</span>,
      headerProps,
      minWidth: 205,
      render: ({ data }) => {
        const { tab, setGeneContent } = searchContext();
        let ids = [];
        let values = [];

        if (tab === 'GDA' || tab === 'DDA') {
          ids = data.geneProteinStrIDs;
          values = data.geneProteinStrIDs;
        }

        if (!ids?.length) return <span className="geneModalLink">-</span>;
        const geneId = getValidGeneInfoId(data);
        const symbol = String(getGeneMappingName(data) ?? data?.symbol ?? data?.geneSymbol ?? '');
        return (
          <span className="geneModalLink">
            {ids.map((id, index) => {
              const displayValue = values[index] ?? id;
              return (
                <span key={`${id}-${index}`}>
                  {geneId ? (
                    <GeneNamePanelButton geneInfo={{ symbol, id: geneId }} setGeneContent={setGeneContent}>
                      {displayValue}
                    </GeneNamePanelButton>
                  ) : (
                    displayValue
                  )}
                  <span className="px-1">{index !== ids.length - 1 ? '|' : ''}</span>
                </span>
              );
            })}
          </span>
        );
      },
    },

    {
      name: 'proteinClassIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>,
      headerProps,
      minWidth: 200,
      ...createCapitalizeFilter(dropdownLists.proteinClassIDs),
      render: ({ data }) => {
        const proteins = data?.proteins ?? [];
        const uniqueClasses = [...new Set(proteins.flatMap(p => p.proteinClasses?.map(pc => pc.name) ?? []))];
        return uniqueClasses.length ? uniqueClasses.join(' | ') : '-';
      },
    },

    {
      name: 'proteinIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="UniProt Accession">UniProt</span>,
      headerProps,
      minWidth: 150,
      render: instance => {
        const proteinIDs = instance?.data?.proteinIDs;

        const result = renderTextColumn(proteinIDs);
        if (result === '-') return '-';

        return proteinIDs.map((uniprot, index) => (
          <span key={uniprot}>
            <a href={`https://www.uniprot.org/uniprot/${uniprot}`} target="_blank" rel="noopener noreferrer">
              {uniprot}
              <i className="fa fa-external-link" aria-hidden="true"></i>
            </a>
            {index < proteinIDs.length - 1 && ' | '}
          </span>
        ));
      },
    },

    {
      name: 'pathwayID',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways IDs">Pathways IDs</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayID'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'pathwayName',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways Names">Pathways</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayName'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'numDiseasesAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of diseases associated to the gene">
          N diseases<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems: any[] = [];

        if (tab === 'GENES') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [{ filterParam: 'gene_ncbi_id', filterValue: geneId, name: geneName, id: geneId }];
        } else if (tab === 'GDA') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [
            {
              filterParam: 'gene_ncbi_id',
              filterValue: geneId,
              name: geneName,
              id: geneId,
            },
          ];
        } else {
          return renderNumericColumn(data?.numDiseasesAssociatedToGene);
        }

        if (!data.numDiseasesAssociatedToGene) return renderNumericColumn(data?.numDiseasesAssociatedToGene);

        return renderMappingButton(data, {
          displayValue: renderNumericColumn(data.numDiseasesAssociatedToGene),
          mappingItems,
          columVisibilityModelKey: 'numDiseasesAssociatedToGene',
          tabSegment: 'gda',
          name: 'numDiseasesAssociatedToGene',
          viewSegment: '/summary',
        });
      },
    },

    {
      name: 'numVariantsAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of variants associated to the gene">
          N variants<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      show: defineColumnShow(context => {
        const { tab, source } = context;
        if (tab !== 'GENES') return true;

        const sourcesWithoutVariants = ['ORPHANET', 'GENCC', 'MGD_HUMAN', 'PSYGENET', 'RGD_HUMAN'];

        return !sourcesWithoutVariants.includes(source ?? '');
      }),
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems: any[] = [];

        if (tab === 'GENES') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [{ filterParam: 'gene_ncbi_id', filterValue: geneId, name: geneName, id: geneId }];
        } else if (tab === 'GDA') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [
            {
              filterParam: 'gene_ncbi_id',
              filterValue: geneId,
              name: geneName,
              id: geneId,
            },
          ];
        } else {
          return renderNumericColumn(data?.numVariantsAssociatedToGene);
        }

        if (!data.numVariantsAssociatedToGene) return renderNumericColumn(data?.numVariantsAssociatedToGene);

        return renderMappingButton(data, {
          displayValue: renderNumericColumn(data.numVariantsAssociatedToGene),
          mappingItems,
          columVisibilityModelKey: 'numVariantsAssociatedToGene',
          tabSegment: 'vda',
          name: 'numVariantsAssociatedToGene',
          viewSegment: '/summary',
        });
      },
    },
  ];
};
